import { useEffect, useRef, useState } from "react";

interface CityFeature {
  icon: string;
  title: string;
  desc: string;
}

const features: CityFeature[] = [
  { icon: "ri-building-2-line", title: "滨海新城", desc: "现代都市天际线" },
  { icon: "ri-leaf-line", title: "生态宜居", desc: "绿色低碳新生活" },
  { icon: "ri-graduation-cap-line", title: "教育强市", desc: "双一流高校云集" },
  { icon: "ri-flight-takeoff-line", title: "开放枢纽", desc: "国际航运新门户" },
  { icon: "ri-heart-pulse-line", title: "幸福指数", desc: "全国最宜居城市" },
];

interface Stat {
  value: string;
  unit: string;
  label: string;
}

const stats: Stat[] = [
  { value: "531", unit: "万+", label: "常住人口" },
  { value: "6800", unit: "亿", label: "GDP总量" },
  { value: "300+", unit: "天", label: "年均日照" },
  { value: "Top3", unit: "", label: "宜居城市" },
];

interface CityScene {
  title: string;
  subtitle: string;
  image: string;
}

const cityScenes: CityScene[] = [
  {
    title: "滨海新城",
    subtitle: "逐梦之城",
    image: "https://public.readdy.ai/ai/img_res/19ddf44843f9e079ad0ffe0329c7b7f8.jpg",
  },
  {
    title: "环岛骑行",
    subtitle: "活力生活",
    image: "https://readdy.ai/api/search-image?query=People%20cycling%20along%20Xiamen%20coastal%20road%20at%20golden%20hour%2C%20palm%20trees%20lining%20the%20path%2C%20turquoise%20sea%20on%20one%20side%2C%20modern%20city%20buildings%20on%20other%20side%2C%20active%20lifestyle%2C%20warm%20sunlight%2C%20vibrant%20energetic%20atmosphere%2C%20wide%20angle%20photography%2C%20ultra%20detailed%2C%20beautiful%20scenery&width=1920&height=1080&seq=city002&orientation=landscape",
  },
  {
    title: "夜色厦门",
    subtitle: "不夜之城",
    image: "https://readdy.ai/api/search-image?query=Xiamen%20city%20night%20view%20from%20above%2C%20glittering%20city%20lights%20reflecting%20on%20sea%20water%2C%20illuminated%20bridges%20and%20coastal%20roads%2C%20vibrant%20nightlife%20districts%2C%20long%20exposure%20photography%2C%20deep%20blue%20night%20sky%20with%20stars%2C%20ultra%20detailed%2C%20cinematic%20aerial%20view%2C%20stunning%20urban%20landscape&width=1920&height=1080&seq=city003&orientation=landscape",
  },
];

interface CityPageProps {
  visible: boolean;
  onNext: () => void;
  onPrev: () => void;
}

export default function CityPage({ visible, onNext, onPrev }: CityPageProps) {
  const [entered, setEntered] = useState(false);
  const [activeScene, setActiveScene] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [animatedStats, setAnimatedStats] = useState<number[]>([0, 0, 0, 0]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => {
        setEntered(true);
        setTimeout(() => setStatsVisible(true), 400);
      }, 80);
      autoPlayRef.current = setInterval(() => {
        setActiveScene((prev) => (prev + 1) % cityScenes.length);
      }, 3500);
      return () => {
        clearTimeout(t);
        if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      };
    } else {
      setEntered(false);
      setStatsVisible(false);
      setAnimatedStats([0, 0, 0, 0]);
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    }
  }, [visible]);

  useEffect(() => {
    if (!statsVisible) return;
    const targets = [531, 6800, 300, 3];
    let frame = 0;
    const totalFrames = 40;
    intervalRef.current = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedStats(targets.map((t) => Math.round(t * eased)));
      if (frame >= totalFrames) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, 30);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [statsVisible]);

  const handleSceneChange = (i: number) => {
    setActiveScene(i);
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setActiveScene((prev) => (prev + 1) % cityScenes.length);
    }, 3500);
  };

  const statDisplayValues = ["531", "6800", "300+", "Top3"];

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gray-950 flex flex-col">
      {/* Background Scene */}
      <div className="absolute inset-0">
        {cityScenes.map((scene, i) => (
          <div
            key={scene.title}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === activeScene ? "opacity-100" : "opacity-0"}`}
          >
            <img src={scene.image} alt={scene.title} className="w-full h-full object-cover object-top" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
      </div>

      {/* Top Nav */}
      <nav className="relative z-30 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <img
          src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png"
          alt="Logo"
          className="h-8 md:h-9 w-auto object-contain"
        />
        <div className="hidden md:flex items-center gap-6">
          {["山海", "人文", "美食", "活力"].map((item, i) => (
            <span
              key={item}
              className={`text-xs tracking-widest cursor-pointer transition-colors whitespace-nowrap ${i === 3 ? "text-cyan-300 font-semibold" : "text-white/60 hover:text-white"}`}
            >
              {item}
            </span>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-20 flex-1 flex flex-col justify-between px-5 md:px-16 pb-12 md:pb-16">
        {/* Title */}
        <div className={`mt-1 md:mt-4 transition-all duration-800 ${entered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 mb-2 md:mb-4">
            <span className="text-cyan-400 text-xs tracking-[0.25em] font-medium">05 / CITY</span>
            <div className="w-4 md:w-6 h-px bg-cyan-400/50" />
          </div>
          <h2
            className="text-4xl md:text-7xl font-light text-white leading-tight"
            style={{ fontFamily: "'Noto Serif SC', serif" }}
          >
            宜居宜业
          </h2>
          <p className="text-white/60 text-xs md:text-base mt-1 md:mt-3 tracking-widest">
            浪漫宜居，逐梦之城，未来可期
          </p>
        </div>

        {/* Stats */}
        <div
          className={`grid grid-cols-4 gap-2 md:gap-4 max-w-2xl transition-all duration-800 delay-200 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          {stats.map((stat, i) => (
            <div key={stat.label} className="text-center">
              <div className="flex items-end justify-center gap-0.5 mb-0.5 md:mb-1">
                <span
                  className="text-xl md:text-4xl font-light text-white"
                  style={{ fontFamily: "'Noto Sans SC', sans-serif" }}
                >
                  {i === 3 ? statDisplayValues[i] : animatedStats[i]}
                </span>
                {stat.unit && (
                  <span className="text-cyan-400 text-xs mb-0.5 md:mb-1">{stat.unit}</span>
                )}
              </div>
              <p className="text-white/50 text-xs tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Features + Scene Switcher */}
        <div className="flex items-end justify-between">
          {/* Feature Pills */}
          <div
            className={`flex flex-wrap gap-1.5 md:gap-2 max-w-lg transition-all duration-800 delay-300 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            {features.map((f, i) => (
              <div
                key={f.title}
                className="flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-2.5 md:px-4 py-1.5 md:py-2 transition-all duration-300 hover:bg-white/20 cursor-default"
                style={{ transitionDelay: `${i * 60 + 400}ms` }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${f.icon} text-cyan-300 text-xs md:text-sm`} />
                </div>
                <span className="text-white text-xs font-medium whitespace-nowrap">{f.title}</span>
                <span className="text-white/50 text-xs hidden lg:inline whitespace-nowrap">{f.desc}</span>
              </div>
            ))}
          </div>

          {/* Scene Switcher */}
          <div className="flex flex-col items-end gap-2 md:gap-3 flex-shrink-0 ml-2">
            {cityScenes.map((scene, i) => (
              <button
                key={scene.title}
                onClick={() => handleSceneChange(i)}
                className={`flex items-center gap-2 md:gap-3 transition-all duration-300 cursor-pointer group ${i === activeScene ? "opacity-100" : "opacity-40 hover:opacity-70"}`}
              >
                <div className="text-right hidden md:block">
                  <p className="text-white text-xs font-medium whitespace-nowrap">{scene.title}</p>
                  <p className="text-white/50 text-xs whitespace-nowrap">{scene.subtitle}</p>
                </div>
                <div
                  className={`rounded-full transition-all duration-300 ${i === activeScene ? "w-6 md:w-8 h-1.5 bg-cyan-400" : "w-1.5 h-1.5 bg-white/50"}`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={onNext}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-cyan-500/80 backdrop-blur-sm text-white hover:bg-cyan-500 transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Page indicator */}
      <div className="absolute bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`rounded-full transition-all duration-300 ${i === 4 ? "w-6 h-1.5 bg-cyan-400" : "w-1.5 h-1.5 bg-white/30"}`}
          />
        ))}
      </div>
    </div>
  );
}
